import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { type BaseQLSelectionSet, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { MatchingUserAPI } from '@adornis/bh-matching-user/api/MatchingUserAPI.js';
import { checkAuthorizedMiddleware } from '@adornis/bh-matching-user/checkAuthorizedMiddleware.js';
import {
  BuddyUserMentee,
  BuddyUserMentor,
  type DefaultMenteeUser,
  type DefaultMentorUser,
  GenderPreference,
} from '@adornis/bh-matching-user/entities/buddy-user.js';
import { MatchingPermission } from '@adornis/bh-matching-user/permissions.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { Match } from '../entities/match.js';

const _autoMatchUsers = (mentors: DefaultMentorUser[], mentees: DefaultMenteeUser[], existingMatches: Match[] = []) => {
  const newMatches: Match[] = [];
  for (const mentee of mentees ?? []) {
    const _mentors = mentors ?? [];
    // check gender preference
    const filteredMentors = _mentors.filter(mentor => {
      // filter gender if mentee prefers same gender
      if (
        mentee.has('genderPreference') &&
        mentee.has('gender') &&
        mentor.has('gender') &&
        (mentee.genderPreference === GenderPreference.SAME_SEX ||
          mentor.genderPreference === GenderPreference.SAME_SEX) &&
        mentee.gender !== mentor.gender
      ) {
        return false;
      }

      // check spoken languages
      if (
        mentor.has('foreignLanguages') &&
        mentor.has('nativeLanguage') &&
        mentee.has('foreignLanguages') &&
        mentee.has('nativeLanguage') &&
        (mentee.foreignLanguages || mentee.nativeLanguage)
      ) {
        const mentorLangs = [...(mentor.foreignLanguages ?? [])];
        if (mentor.nativeLanguage) mentorLangs.push(mentor.nativeLanguage);
        const menteeLangs = [...(mentee.foreignLanguages ?? [])];
        if (mentee.nativeLanguage) menteeLangs.push(mentee.nativeLanguage);
        if (menteeLangs.filter(lang => mentorLangs.includes(lang)).length < 1) {
          return false;
        }
      }

      return true;
    });

    // sort for lowest matched count  mentors to front
    const existingAndAlreadyCalculatedMatches = existingMatches.concat(newMatches);
    filteredMentors.sort(
      (a: any, b: any) =>
        (a.matches?.length ?? 0) +
        (existingAndAlreadyCalculatedMatches.filter(match => match.mentorID === a._id).length ?? 0) -
        ((b.matches?.length ?? 0) +
          (existingAndAlreadyCalculatedMatches.filter(match => match.mentorID === b._id).length ?? 0)),
    );

    const firstMentor = filteredMentors[0];
    if (firstMentor) {
      newMatches.push(new Match({ mentee, menteeID: mentee._id, mentor: firstMentor, mentorID: firstMentor._id }));
    }
  }

  return newMatches;
};

@Entity()
export class AutoMatchUserEntry extends AdornisEntity {
  static override _class = 'AutoMatchUserEntry';

  @Field(type => String)
  userClass!: string;

  @Field(type => AdornisFilter)
  filter!: AdornisFilter;

  static fromParams(userClass: string, filter: AdornisFilter) {
    return new AutoMatchUserEntry({ userClass, filter });
  }
}

export const autoMatchUsers = registerQuery({
  operationName: 'autoMatchUsers',
  middlewares: [
    checkAuthorizedMiddleware(({ user }) => user.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_MATCHES)),
  ],
  type: () => [Match],
  resolve:
    (timeframeID: string, menteeEntry: AutoMatchUserEntry, mentorEntry: AutoMatchUserEntry) =>
    async (gqlFields: BaseQLSelectionSet<Match>) => {
      const mentees = await MatchingUserAPI.getAllUnsmatchedInCurrentTimeframe(
        menteeEntry.userClass,
        timeframeID,
        menteeEntry.filter,
      )(selectionSet(() => BuddyUserMentee, 4));
      if (!mentees || mentees.length === 0) return [];

      const mentors = await MatchingUserAPI.getAllInCurrentTimeframe(
        mentorEntry.userClass,
        timeframeID,
        mentorEntry.filter,
      )(selectionSet(() => BuddyUserMentor, 4));
      if (!mentors || mentors.length === 0) return [];

      return getAutomatchingAlgorithm()(mentors as any, mentees as any);
    },
  params: [
    { name: 'timeframeID', type: () => ID },
    { name: 'menteeEntry', type: () => AutoMatchUserEntry },
    { name: 'mentorEntry', type: () => AutoMatchUserEntry },
  ],
});

export const automatichingRuleDescription = { text: null as string | null };
let automatchingAlgorithm: typeof _autoMatchUsers = _autoMatchUsers;

export const setAutomatchingAlgorithm = (autoMatchUsers: typeof _autoMatchUsers) => {
  automatchingAlgorithm = autoMatchUsers;
};

export const getAutomatchingAlgorithm = () => automatchingAlgorithm;
